import { CreateUnitPrefillSnapshot } from '../types/createUnitPrefill'
import { Project } from '../types/project'
import { Corporate } from '../types/corporate'

const mockProject = (jobId: string, title: string): Project => ({
  id: 9001,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  deletedAt: null,
  name: title,
  description: null,
  status: 'DRAFT',
  progress: 0,
  externalReference: 'mock',
  vectorStoreType: 'QDRANT',
  vectorStoreUrl: 'https://example.qdrant.io:6333/collections/mock',
  frontend_id: `mock-${jobId}`
})

const mockCorporate = (
  projectId: number,
  partial: Partial<Corporate>
): Corporate => ({
  id: 1,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  deletedAt: null,
  projectId,
  workload: '4h',
  departments: [],
  goal: null,
  competencies: [],
  expectedBehaviors: null,
  indicators: [],
  currentGoal: null,
  structure: [],
  ...partial
})

export const buildMockPrefillSnapshots = (
  jobId: string,
  description: string
): CreateUnitPrefillSnapshot[] => {
  const title =
    description.length > 60 ? `${description.slice(0, 57)}…` : description
  const project = mockProject(jobId, title)
  const base = {
    job_id: jobId,
    project_id: project.id,
    fileUploadStatus: 'NONE' as const,
    contentGenerationStatus: 'NONE' as const,
    currentCreateUnitTab: 'basic' as const
  }

  return [
    {
      ...base,
      step: 'basic',
      step_status: 'COMPLETED',
      overall_status: 'PROCESSING',
      project,
      corporate: mockCorporate(project.id, { workload: '4h' }),
      createUnitData: { title, workload: '4h' },
      generatedDepartmentCompetencies: [],
      generatedDepartmentIndicators: [],
      enabledCreateUnitTabs: ['basic', 'audience'],
      structureGenerationStatus: 'NONE'
    },
    {
      ...base,
      step: 'audience',
      step_status: 'COMPLETED',
      overall_status: 'PROCESSING',
      project,
      corporate: mockCorporate(project.id, {
        workload: '4h',
        departments: ['Comercial', 'Marketing']
      }),
      createUnitData: {
        title,
        workload: '4h',
        departments: ['Comercial', 'Marketing']
      },
      generatedDepartmentCompetencies: ['Comunicação consultiva', 'Negociação'],
      generatedDepartmentIndicators: ['Taxa de conversão', 'NPS'],
      enabledCreateUnitTabs: ['basic', 'audience', 'goal'],
      structureGenerationStatus: 'NONE'
    },
    {
      ...base,
      step: 'goal',
      step_status: 'COMPLETED',
      overall_status: 'PROCESSING',
      project,
      corporate: mockCorporate(project.id, {
        workload: '4h',
        departments: ['Comercial', 'Marketing'],
        goal: 'Capacitar a equipe em vendas consultivas'
      }),
      createUnitData: {
        title,
        workload: '4h',
        departments: ['Comercial', 'Marketing'],
        goal: 'Capacitar a equipe em vendas consultivas'
      },
      generatedDepartmentCompetencies: ['Comunicação consultiva', 'Negociação'],
      generatedDepartmentIndicators: ['Taxa de conversão', 'NPS'],
      enabledCreateUnitTabs: ['basic', 'audience', 'goal', 'competencies'],
      structureGenerationStatus: 'NONE'
    },
    {
      ...base,
      step: 'competencies',
      step_status: 'COMPLETED',
      overall_status: 'PROCESSING',
      project,
      corporate: mockCorporate(project.id, {
        workload: '4h',
        departments: ['Comercial', 'Marketing'],
        goal: 'Capacitar a equipe em vendas consultivas',
        competencies: ['Comunicação consultiva', 'Negociação'],
        expectedBehaviors: 'Abordagem centrada no cliente'
      }),
      createUnitData: {
        title,
        workload: '4h',
        departments: ['Comercial', 'Marketing'],
        goal: 'Capacitar a equipe em vendas consultivas',
        competencies: ['Comunicação consultiva', 'Negociação'],
        expectedBehaviors: 'Abordagem centrada no cliente'
      },
      generatedDepartmentCompetencies: ['Comunicação consultiva', 'Negociação'],
      generatedDepartmentIndicators: ['Taxa de conversão', 'NPS'],
      enabledCreateUnitTabs: [
        'basic',
        'audience',
        'goal',
        'competencies',
        'indicators'
      ],
      structureGenerationStatus: 'NONE'
    },
    {
      ...base,
      step: 'indicators',
      step_status: 'COMPLETED',
      overall_status: 'PROCESSING',
      project,
      corporate: mockCorporate(project.id, {
        workload: '4h',
        departments: ['Comercial', 'Marketing'],
        goal: 'Capacitar a equipe em vendas consultivas',
        competencies: ['Comunicação consultiva', 'Negociação'],
        expectedBehaviors: 'Abordagem centrada no cliente',
        indicators: ['Taxa de conversão', 'NPS'],
        currentGoal: 'Aumentar conversão em 15%'
      }),
      createUnitData: {
        title,
        workload: '4h',
        departments: ['Comercial', 'Marketing'],
        goal: 'Capacitar a equipe em vendas consultivas',
        competencies: ['Comunicação consultiva', 'Negociação'],
        expectedBehaviors: 'Abordagem centrada no cliente',
        indicators: ['Taxa de conversão', 'NPS'],
        currentGoal: 'Aumentar conversão em 15%'
      },
      generatedDepartmentCompetencies: ['Comunicação consultiva', 'Negociação'],
      generatedDepartmentIndicators: ['Taxa de conversão', 'NPS'],
      enabledCreateUnitTabs: [
        'basic',
        'audience',
        'goal',
        'competencies',
        'indicators',
        'material',
        'structure'
      ],
      structureGenerationStatus: 'PROCESSING'
    },
    {
      ...base,
      step: 'structure',
      step_status: 'COMPLETED',
      overall_status: 'COMPLETED',
      project,
      corporate: mockCorporate(project.id, {
        workload: '4h',
        departments: ['Comercial', 'Marketing'],
        goal: 'Capacitar a equipe em vendas consultivas',
        competencies: ['Comunicação consultiva', 'Negociação'],
        expectedBehaviors: 'Abordagem centrada no cliente',
        indicators: ['Taxa de conversão', 'NPS'],
        currentGoal: 'Aumentar conversão em 15%',
        structure: [
          'Introdução às vendas consultivas',
          'Técnicas de descoberta de necessidades',
          'Fechamento e follow-up'
        ]
      }),
      createUnitData: {
        title,
        workload: '4h',
        departments: ['Comercial', 'Marketing'],
        goal: 'Capacitar a equipe em vendas consultivas',
        competencies: ['Comunicação consultiva', 'Negociação'],
        expectedBehaviors: 'Abordagem centrada no cliente',
        indicators: ['Taxa de conversão', 'NPS'],
        currentGoal: 'Aumentar conversão em 15%',
        structure: [
          'Introdução às vendas consultivas',
          'Técnicas de descoberta de necessidades',
          'Fechamento e follow-up'
        ]
      },
      generatedDepartmentCompetencies: ['Comunicação consultiva', 'Negociação'],
      generatedDepartmentIndicators: ['Taxa de conversão', 'NPS'],
      enabledCreateUnitTabs: [
        'basic',
        'audience',
        'goal',
        'competencies',
        'indicators',
        'material',
        'structure'
      ],
      structureGenerationStatus: 'COMPLETED'
    }
  ]
}

export const simulateMockPrefill = (
  jobId: string,
  description: string,
  onSnapshot: (snapshot: CreateUnitPrefillSnapshot) => void,
  onComplete: () => void
): (() => void) => {
  const snapshots = buildMockPrefillSnapshots(jobId, description)
  const timeouts: ReturnType<typeof setTimeout>[] = []

  snapshots.forEach((snapshot, index) => {
    const timeout = setTimeout(() => {
      onSnapshot(snapshot)
      if (snapshot.overall_status === 'COMPLETED') {
        onComplete()
      }
    }, (index + 1) * 800)
    timeouts.push(timeout)
  })

  return () => {
    timeouts.forEach(clearTimeout)
  }
}
